/*
 * Audit History (iter323 + iter324) — Admin-only Complete Activity History.
 *
 * iter324 additions (UI-only):
 *   - Module + Account/Property dropdowns on the top filter bar.
 *   - Human-readable field labels (totalRooms → Rooms, gstNumber → GST Number …).
 *   - Defensive summarizer that shortens raw JSON/object/array values that
 *     were captured before the backend-side summarizer landed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, ChevronLeft, ChevronRight, History } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const _fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};
const _fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

// iter324 — Human labels for common technical field names. Anything not
// in the map falls back to a generic camelCase / snake_case split.
const FIELD_LABELS = {
  totalRooms: 'Rooms',
  total_rooms: 'Rooms',
  roomCount: 'Room Count',
  room_count: 'Room Count',
  rooms: 'Rooms',
  city: 'City',
  state: 'State',
  address: 'Address',
  pincode: 'Pincode',
  hotel_name: 'Property Name',
  hotelName: 'Property Name',
  official_property_name: 'Property Name',
  propertyName: 'Property Name',
  property_type: 'Property Type',
  propertyType: 'Property Type',
  property_status: 'Status',
  propertyStatus: 'Status',
  lead_stage: 'Stage',
  leadStage: 'Stage',
  lead_owner: 'Owner',
  leadOwner: 'Owner',
  owner_name: 'Owner Name',
  ownerName: 'Owner Name',
  phone: 'Phone',
  email: 'Email',
  gst_number: 'GST Number',
  gstNumber: 'GST Number',
  pan_number: 'PAN Number',
  panNumber: 'PAN Number',
  check_in_time: 'Check-in Time',
  checkInTime: 'Check-in Time',
  check_out_time: 'Check-out Time',
  checkOutTime: 'Check-out Time',
  number_of_rooms: 'Rooms',
  numberOfRooms: 'Rooms',
  category: 'Category',
  ota_info: 'OTAs',
  otaInfo: 'OTAs',
  amenities: 'Amenities',
  documents: 'Documents',
  bankDetails: 'Bank Details',
  bank_details: 'Bank Details',
  account_number: 'Bank A/C Number',
  accountNumber: 'Bank A/C Number',
  ifsc_code: 'IFSC',
  ifscCode: 'IFSC',
  bank_name: 'Bank Name',
  bankName: 'Bank Name',
};

const humaniseField = (f) => {
  if (!f) return '';
  if (FIELD_LABELS[f]) return FIELD_LABELS[f];
  // camelCase → Camel Case; snake_case → Snake Case
  return String(f)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// iter324 — Summarize any value coming out of the audit record so the
// Details column never renders raw JSON. Handles: null, string, number,
// stringified JSON (list/dict), Python-style dict/list repr, and objects
// that somehow made it through. Complex fields are collapsed into
// "N items" / "{k1,k2,…}" / "…updated".
const _summarizeValue = (v) => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? '' : 's'}`;
  if (typeof v === 'object') {
    const keys = Object.keys(v).slice(0, 4);
    return '{' + keys.join(',') + (Object.keys(v).length > 4 ? ',…' : '') + '}';
  }
  const s = String(v);
  // Try to detect a JSON-ish blob (starts with [ or { and is longish).
  const trimmed = s.trim();
  if ((trimmed.startsWith('[') || trimmed.startsWith('{')) && trimmed.length > 40) {
    try {
      const parsed = JSON.parse(trimmed);
      return _summarizeValue(parsed);
    } catch (_) {
      // Not valid JSON — could be a Python repr. Still summarize.
      if (trimmed.startsWith('[')) {
        // crude item count for '[a, b, c]' or list-of-dicts repr
        const commaCount = (trimmed.match(/^\s*\{/gm) || trimmed.match(/,\s*\{/g) || []).length;
        return commaCount > 0 ? `${Math.max(1, commaCount)} item${commaCount === 1 ? '' : 's'}` : 'list';
      }
      return 'updated';
    }
  }
  // Truncate anything > 60 chars.
  return s.length > 60 ? s.slice(0, 60) + '…' : s;
};

const _isBlobField = (fieldName, old, neu) => {
  if (!fieldName) return false;
  const knownBlobs = ['rooms', 'amenities', 'documents', 'ota_info', 'otaInfo', 'bankDetails', 'bank_details'];
  if (knownBlobs.includes(fieldName)) return true;
  const looksBig = (v) => {
    if (typeof v === 'string' && v.length > 60 && (v.trim().startsWith('[') || v.trim().startsWith('{'))) return true;
    if (Array.isArray(v)) return true;
    if (v && typeof v === 'object') return true;
    return false;
  };
  return looksBig(old) || looksBig(neu);
};

const ChangeList = ({ row }) => {
  if (Array.isArray(row.changes) && row.changes.length) {
    return (
      <ul className="space-y-0.5">
        {row.changes.slice(0, 8).map((c, i) => {
          const label = humaniseField(c.field);
          if (_isBlobField(c.field, c.old, c.new)) {
            // For known/blob-like fields — show only a compact summary line.
            return (
              <li key={i} className="text-xs text-gray-700">
                <span className="font-medium">{label}:</span>{' '}
                <span className="text-gray-500 line-through">{_summarizeValue(c.old)}</span>{' '}
                <span className="text-gray-400">→</span>{' '}
                <span className="text-emerald-700">{_summarizeValue(c.new)}</span>
              </li>
            );
          }
          return (
            <li key={i} className="text-xs text-gray-700">
              <span className="font-medium">{label}:</span>{' '}
              <span className="text-gray-500 line-through">{_summarizeValue(c.old)}</span>{' '}
              <span className="text-gray-400">→</span>{' '}
              <span className="text-emerald-700">{_summarizeValue(c.new)}</span>
            </li>
          );
        })}
        {row.changes.length > 8 && (
          <li className="text-[11px] text-gray-400">+{row.changes.length - 8} more field(s)</li>
        )}
      </ul>
    );
  }
  // Free-text `details` — if it's an old record with a huge JSON blob,
  // collapse it before rendering.
  const raw = row.details || '';
  const trimmed = String(raw).trim();
  if ((trimmed.startsWith('[') || trimmed.startsWith('{')) && trimmed.length > 120) {
    return <span className="text-xs text-gray-500 italic">Data updated</span>;
  }
  if (trimmed.length > 200) {
    return <span className="text-xs text-gray-600" title={trimmed}>{trimmed.slice(0, 200)}…</span>;
  }
  return <span className="text-xs text-gray-600">{trimmed || '—'}</span>;
};

const AuditHistory = () => {
  const { user } = useAuth();
  const isAdmin = String(user?.role || '').toLowerCase() === 'admin' ||
    (Array.isArray(user?.roles) && user.roles.some(r => String(r).toLowerCase() === 'admin'));

  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [userFilter, setUserFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [dateMode, setDateMode] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingStart, setPendingStart] = useState('');
  const [pendingEnd, setPendingEnd] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const loadUsers = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/audit-history/users`, { headers: authHeader() });
      if (r.ok) {
        const b = await r.json();
        setUsers(b.users || []);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/audit-history/options`, { headers: authHeader() });
      if (r.ok) {
        const b = await r.json();
        setModules(b.modules || []);
        setAccounts(b.accounts || []);
      }
    } catch (_) { /* ignore */ }
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('user_id', userFilter || 'all');
      p.set('account_id', accountFilter || 'all');
      p.set('module', moduleFilter || 'all');
      p.set('date_mode', dateMode);
      if (dateMode === 'custom' && startDate && endDate) {
        p.set('start_date', startDate);
        p.set('end_date', endDate);
      }
      p.set('page', String(page));
      p.set('page_size', String(pageSize));
      const r = await fetch(`${API_BASE}/api/audit-history?${p.toString()}`, { headers: authHeader() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      toast.error('Failed to load Audit History');
    } finally {
      setLoading(false);
    }
  }, [userFilter, accountFilter, moduleFilter, dateMode, startDate, endDate, page, pageSize]);

  useEffect(() => { if (isAdmin) { loadUsers(); loadOptions(); } }, [isAdmin, loadUsers, loadOptions]);
  useEffect(() => { if (isAdmin) loadRows(); }, [isAdmin, loadRows]);

  // Reset page when any filter changes.
  useEffect(() => { setPage(1); }, [userFilter, accountFilter, moduleFilter, dateMode, startDate, endDate]);

  const totalPages = data ? Math.max(1, data.total_pages) : 1;

  if (!isAdmin) {
    return (
      <div className="p-8 text-center text-gray-500" data-testid="audit-history-forbidden">
        <History className="mx-auto mb-3 text-gray-300" size={40}/>
        <p className="text-sm">Audit History is available to Admins only.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-history-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="text-teal-600" size={24}/> Audit History
        </h1>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl p-3">
        {/* Left group: User, Account/Property, Module */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[160px]"
            data-testid="audit-user-filter"
          >
            <option value="all">All Users</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
          <select
            value={accountFilter}
            onChange={(e) => setAccountFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[220px] max-w-[300px]"
            data-testid="audit-account-filter"
          >
            <option value="all">All Accounts / Properties</option>
            <option value="global">Global (no account)</option>
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[160px]"
            data-testid="audit-module-filter"
          >
            <option value="all">All Modules</option>
            {modules.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>

        {/* Right: date */}
        <div className="flex items-center gap-2">
          {dateMode === 'custom' && (
            <>
              <input type="date" value={pendingStart} onChange={(e) => setPendingStart(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="audit-start-date"/>
              <span className="text-xs text-gray-400">to</span>
              <input type="date" value={pendingEnd} onChange={(e) => setPendingEnd(e.target.value)}
                className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm" data-testid="audit-end-date"/>
              <button
                onClick={() => { setStartDate(pendingStart); setEndDate(pendingEnd); }}
                disabled={!pendingStart || !pendingEnd}
                className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 disabled:opacity-60"
                data-testid="audit-apply-date">
                Apply
              </button>
            </>
          )}
          <select
            value={dateMode}
            onChange={(e) => {
              setDateMode(e.target.value);
              if (e.target.value !== 'custom') {
                setStartDate(''); setEndDate('');
                setPendingStart(''); setPendingEnd('');
              }
            }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[120px]"
            data-testid="audit-date-mode"
          >
            <option value="all">All</option>
            <option value="custom">Custom</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
          <button onClick={loadRows} className="p-1.5 text-gray-500 hover:text-teal-600 hover:bg-gray-100 rounded"
            title="Refresh" data-testid="audit-refresh">
            <RefreshCw size={14}/>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {['Date', 'Time', 'User', 'Account / Property', 'Module', 'Action', 'Details'].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-gray-400"><Loader2 className="animate-spin inline mr-2" size={14}/> Loading…</td></tr>
              )}
              {!loading && (!data || data.data.length === 0) && (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-gray-400">No activity found for the selected filters.</td></tr>
              )}
              {!loading && data?.data.map(r => (
                <tr key={r.id} className="odd:bg-white even:bg-gray-50 border-t border-gray-100" data-testid={`audit-row-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs">{_fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-xs">{_fmtTime(r.created_at)}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-800 text-xs font-medium">{r.user_name || 'System'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700 text-xs">{r.account_display || 'Global'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700 text-xs">{r.module || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full border bg-teal-50 text-teal-700 border-teal-200">{r.action || '—'}</span>
                  </td>
                  <td className="px-3 py-2 text-xs max-w-md"><ChangeList row={r}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Showing {(data.page - 1) * data.page_size + 1}–{Math.min(data.page * data.page_size, data.total)} of {data.total} events</span>
          <div className="flex items-center gap-1">
            <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 flex items-center gap-1"
              data-testid="audit-prev"><ChevronLeft size={12}/> Prev</button>
            <span className="px-2">Page {data.page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50 flex items-center gap-1"
              data-testid="audit-next">Next <ChevronRight size={12}/></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditHistory;
